import React, { useState } from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { withFormik, FormikProps } from 'formik';
import {
  Grid,
  Box,
  makeStyles,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import { Info } from 'react-feather';

import { translate } from 'helpers/intl';
import SelectInputField from 'components/SelectInputField';
import InputField from 'components/InputField';
import validationSchema from 'containers/SignupPages/AuthByContract/ValidationSchema';
import messages from 'containers/SignupPages/AuthByContract/messages';
import ActionsButtons from 'containers/SignupPages/ActionsButtons';
import { ContractType } from 'containers/SignupPages/AuthByContract';
import { OrangeButton } from 'components/Button';
import Modal from 'components/Modal';
import {
  SizedLongBody,
  StyledBodyText,
  StyledPic,
  StyledPicResponsive,
} from 'containers/SignupPages/AuthByContract/styles';
import { AssuranceIcon } from 'images';
import { EVENT_CATEGORIES } from 'helpers/GATracking';
import GaClickHOC from 'helpers/GATracking/GaClickHOC';
import { PRIVILEGES } from 'utils/globalConst';
import HealthInsuranceFields from '../PersonalInfo/HealthInsuranceFields';

const useStyles = makeStyles({
  infoIcon: {
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(0,0,0,0.04)',
      borderRadius: '50%',
    },
  },
});
const AuthByContractForm = (
  props: {
    isLoading: boolean;
    getContractType: Function;
    contractType: ContractType[];
    onClickPrevious: Function;
    typeContractInfos: string;
    numPolice: string;
    dateEcheance?: string;
    values: Record<string, any>;
  } & FormikProps<FormValues>,
) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleOpenModal = () => {
    setOpen(true);
  };
  const isNotMobile = useMediaQuery('(min-width:960px)');

  const handleCloseModal = () => {
    setOpen(false);
  };
  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isLoading,
    onClickPrevious,
    setFieldValue,
  } = props;

  return (
    <form onSubmit={handleSubmit}>
      <Box mb={3} paddingX={[4, 8, 12]}>
        <Grid container spacing={3} justify="center">
          <Grid item xs={12}>
            <SelectInputField
              SelectDisplayProps={{
                'data-testid': 'selectTypeAssurance',
              }}
              name="typeAssurance"
              label={translate({ ...messages.typeAssurance }, props)}
              value={values.typeAssurance}
              handleChange={handleChange}
              handleBlur={handleBlur}
              data={props.contractType}
              onChange={handleChange}
              error={errors.typeAssurance && touched.typeAssurance}
              helperText={touched.typeAssurance && errors.typeAssurance}
              Children={[...props.contractType]
                .reverse()
                .filter((contract: any) => contract.type !== 'EPARGN')
                .map((item: any) => (
                  <MenuItem value={item.type} key={item.value}>
                    {item.value}
                  </MenuItem>
                ))}
            />
          </Grid>
          {values.typeAssurance && (
            <>
              {values.typeAssurance !== PRIVILEGES.AMC ? (
                <>
                  <Grid item xs={6}>
                    <InputField
                      name="numPolice"
                      label={translate({ ...messages.numPolice }, props)}
                      value={values.numPolice}
                      error={errors.numPolice && touched.numPolice}
                      helperText={touched.numPolice && errors.numPolice}
                      endAdornment={
                        <GaClickHOC
                          category={EVENT_CATEGORIES.LOGIN_CONTRACT}
                          label="Aide (i) aide contextuelle "
                        >
                          <Info
                            data-testid="infoIcon"
                            className={classes.infoIcon}
                            onClick={handleOpenModal}
                          />
                        </GaClickHOC>
                      }
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InputField
                      name="dateEffet"
                      label={translate({ ...messages.dateEffet }, props)}
                      value={values.dateEffet}
                      type="date"
                      placeholder="date effet"
                      error={errors.dateEffet && touched.dateEffet}
                      helperText={touched.dateEffet && errors.dateEffet}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        'data-testid': 'dateTest',
                        inputProps: { max: '9999-12-30' },
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <HealthInsuranceFields
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    organization={values.organization}
                    membershipNumber={values.membershipNumber}
                    touched={touched}
                    drpp={values.drpp}
                    errors={errors}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
        <Box marginBottom={2}></Box>
      </Box>
      <Modal
        open={open}
        handleOpen={handleOpenModal}
        handleClose={handleCloseModal}
        header={
          <SizedLongBody weight="500" color="#04336B">
            {translate(messages.modalHeader, props)}
          </SizedLongBody>
        }
        content={
          <>
            <Box paddingX={2}>
              <StyledBodyText>
                {translate(messages.modalContent, props)}
              </StyledBodyText>
              <Box marginTop={3} marginBottom={3}>
                <Grid container justify="center">
                  <Grid item>
                    {isNotMobile ? (
                      <StyledPic src={AssuranceIcon.policyNumberImage} />
                    ) : (
                      <StyledPicResponsive
                        src={AssuranceIcon.policyNumberImage}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </>
        }
        footer={
          <Grid container justify="flex-end">
            <Grid item>
              {' '}
              <OrangeButton variant="contained" onClick={handleCloseModal}>
                {translate(messages.close, props)}
              </OrangeButton>
            </Grid>
          </Grid>
        }
      ></Modal>
      <ActionsButtons
        nextGACategory={EVENT_CATEGORIES.LOGIN_CONTRACT}
        onClickPrevious={onClickPrevious}
        isLoading={isLoading}
      />
    </form>
  );
};

export interface FormProps {
  intl: IntlShape;
  isLoading: boolean;
  getContractType: Function;
  contractType: ContractType[];
  onClickPrevious: Function;
  onSubmitStep: Function;
  typeContractInfos: string;
  numPolice: string;
  dateEcheance?: string;
}

interface FormValues {
  typeAssurance: string;
  numPolice: string;
  dateEffet: string;
  organization?: any;
  membershipNumber: string;
  drpp: string;
}

const Form = withFormik<FormProps, FormValues>({
  mapPropsToValues: (props) => {
    return {
      typeAssurance: props.typeContractInfos || '',
      numPolice: props.numPolice || '',
      dateEffet: props.dateEcheance || '',
      drpp: '',
      membershipNumber: '',
      organization: null,
    };
  },
  validationSchema,
  handleSubmit: (
    {
      typeAssurance,
      numPolice,
      dateEffet,
      drpp,
      membershipNumber = '',
      organization,
    },
    { props },
  ) => {
    props.onSubmitStep({
      typeAssurance,
      numPolice,
      dateEffet,
      organisme: organization?.policyNumber ?? '',
      numAdhesion: membershipNumber,
      drpp,
    });
  },
})(AuthByContractForm);

export default injectIntl(Form);
